/**
 * The frame every compliance section sits in: the scroll container and the
 * title block. An optional review header stays below the layout's global
 * topbar and outside the scrolling content.
 *
 * The header's search value reaches the section through a render prop so what
 * a query *means* stays the section's business (a policy name here, a
 * connector name there), while where the box lives is not.
 *
 * @module components/regulation/RegulationPageShell
 */
import { Fragment, useLayoutEffect, useRef, type ReactNode } from "react";

import { softBreakSegments } from "~/lib/softBreaks";
import { cn } from "~/lib/utils";
import { ClampedText } from "./ClampedText";

import { useRegulationSearch } from "./regulationSearch";

export function RegulationPageShell({
  eyebrow,
  title,
  titleAside,
  artwork,
  description,
  meta,
  actions,
  children,
  footer,
  floatingFooter = false,
  stickyHeader,
}: {
  /** Above the title: a back link or breadcrumb tail. */
  readonly eyebrow?: ReactNode;
  readonly title: string;
  /** Official issuer artwork for a regime detail page. */
  readonly artwork?: ReactNode;
  /** Beside the title on its baseline — a status pill, typically. */
  readonly titleAside?: ReactNode;
  readonly description?: ReactNode | undefined;
  /** A facts strip under the description. */
  readonly meta?: ReactNode;
  /** The page's primary action, aligned with its title. */
  readonly actions?: ReactNode;
  readonly footer?: ReactNode;
  readonly floatingFooter?: boolean;
  /** Replaces the visible title block with controls pinned above the content. */
  readonly stickyHeader?: ReactNode;
  readonly children: (search: string) => ReactNode;
}) {
  const search = useRegulationSearch();
  const footerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const footerElement = footerRef.current;
    const scroll = scrollRef.current;
    if (!floatingFooter || !footerElement || !scroll) return;
    const measure = () => {
      scroll.style.paddingBottom = `${footerElement.getBoundingClientRect().height}px`;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(footerElement);
    return () => {
      observer.disconnect();
      scroll.style.removeProperty("padding-bottom");
    };
  }, [floatingFooter]);

  // The canvas is the theme's own background, inherited from the layout —
  // the same surface the policies list, the sidebar and the header sit on.
  // An earlier version tinted it so white cards would read as cards, but a
  // tint the theme does not own is a different colour in every theme: in
  // the product's dark theme it turned these pages into a grey slab inside a
  // black app. Cards read as cards by their hairline and shadow instead.
  return (
    <div data-regulation-page="true" className="relative flex min-h-0 flex-1 flex-col">
      {stickyHeader ? (
        <header
          data-review-sticky-header
          className="relative z-30 shrink-0 bg-background px-[var(--review-header-inset)] [--review-header-inset:1.25rem] sm:[--review-header-inset:2rem]"
        >
          {stickyHeader}
        </header>
      ) : null}
      <div
        ref={scrollRef}
        className="regulation-page-scroll-fade min-h-0 flex-1 overflow-y-auto"
        style={stickyHeader ? { maskImage: "none", WebkitMaskImage: "none" } : undefined}
      >
        <div className="w-full px-5 py-6 sm:px-8">
          {stickyHeader ? (
            <h1 className="sr-only">{title}</h1>
          ) : (
            // The header measures itself, not the window: the sidebars take
            // width long before the window is narrow. Wide, the actions sit
            // beside the title; narrow, they move up beside the back link (or
            // below the facts when there is none), so the title keeps the
            // full width. The facts strip spans both columns, so it never
            // wraps early against the actions. `page-header` is also the
            // container the actions and the strip query for compact forms.
            <div className="@container/page-header">
              <div data-page-header="" className={cn("grid items-start", headerGrid(Boolean(eyebrow), Boolean(actions)))}>
                {eyebrow ? (
                  <div
                    className={cn(
                      "mb-3 [grid-area:eyebrow]",
                      // Narrow, it shares a row with the actions: as tall as
                      // their buttons, the link centred on them.
                      actions && "mb-2 flex min-h-8 items-center @[34rem]/page-header:mb-3 @[34rem]/page-header:block @[34rem]/page-header:min-h-0",
                    )}
                  >
                    {eyebrow}
                  </div>
                ) : null}
                <div className="min-w-0 [grid-area:title]">
                  <div className="flex items-start gap-3">
                    {artwork ? <div className="shrink-0 pt-0.5">{artwork}</div> : null}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <h1 className="min-w-0 text-xl font-semibold tracking-tight text-pretty text-foreground [overflow-wrap:anywhere]">
                          <SoftBreakText text={title} />
                        </h1>
                        {titleAside}
                      </div>
                    </div>
                  </div>
                  {description ? <div className="mt-2 text-sm"><ClampedText className="text-muted-foreground">{description}</ClampedText></div> : null}
                </div>
                {meta ? <div className="mt-3 min-w-0 [grid-area:meta]">{meta}</div> : null}
                {actions ? (
                  <div
                    className={cn(
                      "shrink-0 [grid-area:actions]",
                      eyebrow
                        ? "mb-2 self-center justify-self-end @[34rem]/page-header:mb-0 @[34rem]/page-header:self-start"
                        : "mt-4 justify-self-start @[34rem]/page-header:mt-0 @[34rem]/page-header:justify-self-end",
                    )}
                  >
                    {actions}
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {children(search)}
          {stickyHeader ? (
            // The height a collapsing review header has given to this
            // viewport, so the scroll range stays fixed while it collapses.
            <div
              aria-hidden
              data-review-header-give-back
              style={{ height: "var(--review-header-give-back, 0px)" }}
            />
          ) : null}
        </div>
      </div>
      {floatingFooter ? (
        <div ref={footerRef} className="pointer-events-none absolute inset-x-0 bottom-0 z-20">
          {footer}
        </div>
      ) : (
        footer
      )}
    </div>
  );
}

/** A title that may wrap between the words of a filename-style name. */
function SoftBreakText({ text }: { readonly text: string }) {
  const segments = softBreakSegments(text);
  if (segments.length === 1) return text;
  return segments.map((segment, index) => (
    <Fragment key={`${index}:${segment}`}>
      {index > 0 ? <wbr /> : null}
      {segment}
    </Fragment>
  ));
}

/**
 * The header's grid: which area sits where, narrow and wide (34rem of the
 * header). Margins, not row gaps, space the rows, so an area a page does not
 * fill takes no room.
 */
function headerGrid(eyebrow: boolean, actions: boolean): string {
  if (!actions) return "grid-cols-1 [grid-template-areas:'eyebrow'_'title'_'meta']";
  return cn(
    "grid-cols-[minmax(0,1fr)_auto] gap-x-4",
    eyebrow
      ? "[grid-template-areas:'eyebrow_actions'_'title_title'_'meta_meta'] @[34rem]/page-header:[grid-template-areas:'eyebrow_eyebrow'_'title_actions'_'meta_meta']"
      : "[grid-template-areas:'title_title'_'meta_meta'_'actions_actions'] @[34rem]/page-header:[grid-template-areas:'title_actions'_'meta_meta']",
  );
}
